import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { X, ChevronLeft, ChevronRight, CheckCircle2, HelpCircle, Sparkles } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Role-specific guided walkthrough (Phase 2F)
// Frontend-only. No backend, no new collections, no RBAC changes.
// Completion is recorded per-user per-role in localStorage.
// ─────────────────────────────────────────────────────────────────────────────
const TOUR_VERSION = "v1";

const TOURS = {
  platform_owner: {
    title: "Welcome, Platform Owner",
    intro: "You oversee every organisation on Engage. Here is what the platform surfaces for you first.",
    steps: [
      { title: "Platform Health", body: "The Live Readiness card on your dashboard shows whether Stripe, Resend, webhooks, test-email and delivery providers are configured. Five checks, one verdict." },
      { title: "Organisations", body: "Every organisation appears in your tenant list with usage, plan and trial status. Click into any organisation to see its operational picture." },
      { title: "Billing", body: "Calculate, project and dispatch monthly bills. Failed payments and recovery sit in one workbench. Every billing event is logged." },
      { title: "Live Readiness · email outcomes", body: "Open the 24-hour email outcomes drill-down to see which categories sent, failed or were skipped. Click any tile to inspect the events directly." },
    ],
    cta: "Start with the Live Readiness card",
  },
  owner: {
    title: "Welcome to your operation",
    intro: "You see everything happening across your campaigns, teams and fundraisers — in one place.",
    steps: [
      { title: "Operational Record", body: "Open the Operational Record (green button in the header) to see the unified picture — health, momentum, lifecycle, forecast and what needs attention. Same numbers your charity partners see." },
      { title: "Campaigns & Team", body: "Every active campaign, location and fundraiser lives under the Campaigns, Team and Workforce tabs. Activity feeds in live as fundraisers work." },
      { title: "Manager Scorecards", body: "Your Overview surfaces every manager's grade, their coachable fundraisers and who's outperforming — the action layer above the read layer." },
      { title: "Delivery & Cancellations", body: "Delivery, Sign-Ups, Flagged and Cancellations tabs are where you take action when the Operational Record surfaces something." },
      { title: "Board Packs", body: "Generate a print-ready board pack any time from the Operational Record or Hero strip. Auto-dispatch monthly to charity partners — every send is event-logged." },
    ],
    cta: "Open your dashboard",
  },
  manager: {
    title: "Welcome, Manager",
    intro: "You see your subtree of the operation — your team, your campaigns, your coverage.",
    steps: [
      { title: "Operational Record", body: "Open the Operational Record (green button in the header) for the unified picture — health, momentum, forecast, schedule gap and coverage. Scoped to your subtree." },
      { title: "Team performance", body: "The Team and Workforce tabs let you compare fundraisers, venues and shifts. The scorecard surface tells you who is improving, who is stable and who is cooling." },
      { title: "Schedule", body: "Shift Planner and Assignments are where you build the rota the Operational Record's forecast asks for." },
      { title: "Sign-Ups & Flagged", body: "Sign-Ups, Flagged and Cancellations tabs are your action surfaces for everything the Operational Record surfaces." },
    ],
    cta: "Take me to my team",
  },
  team_leader: {
    title: "Welcome, Team Leader",
    intro: "Your day-to-day picture: team activity, daily targets, and where to coach.",
    steps: [
      { title: "Team activity", body: "See every conversation, signup and outcome your team has logged today. Live, no end-of-day spreadsheet." },
      { title: "Forecasting", body: "The week's expected outcome based on your team's recent activity — not a guess, not a quota." },
      { title: "Coaching visibility", body: "Per-fundraiser momentum tells you who needs a conversation today. No surprises at the next one-to-one." },
    ],
    cta: "Open today's view",
  },
  fundraiser: {
    title: "Welcome to your shift",
    intro: "Your dashboard is built around your day — schedule, performance, and where you are against target.",
    steps: [
      { title: "Schedule", body: "Your shifts, venues and campaigns sit at the top of your dashboard. Tap any shift to see the brief." },
      { title: "Performance", body: "Your conversions, signups and recent outcomes are visible to you any time. Same numbers your team leader sees." },
      { title: "Targets", body: "Your daily and weekly targets are explicit. Progress against them updates live as you log activity." },
    ],
    cta: "Start my day",
  },
  client: {
    title: "Welcome to your Client Portal",
    intro: "Your trustee-facing operational record — the same numbers your agency partner works from, scoped to what matters to you.",
    steps: [
      { title: "Open the Operational Record", body: "Click the green Operational Record button on this page to see the full operational narrative — health, forecast, momentum and what needs attention. The same shared record every role sees." },
      { title: "Trustee KPIs at a glance", body: "Active campaigns, fundraisers, signups, delivery success, retention rate and your top-performing campaign — board-ready numbers, every time you log in." },
      { title: "Attention Required", body: "If donors are cancelling, the Cancellation Reasons card explains why — failed payment, donor request, compliance or other — so you know where to focus." },
      { title: "Operational coverage", body: "The heat map shows where fundraising activity is happening across today, this week, this month, the quarter and the year. Switch period to compare." },
      { title: "Board packs", body: "Generate a trustee-ready board pack any time with the Generate Board Pack button. Same content your agency exports. PDF and CSV exports also available." },
    ],
    cta: "Open my portal",
  },
};

function resolveTourKey(user) {
  if (!user) return null;
  const role = user.role;
  if (role === "platform_owner") return "platform_owner";
  if (role === "owner") return "owner";
  if (role === "manager") return "manager";
  if (role === "client") return "client";
  if (role === "fundraiser") {
    const teamRole = (user.teamRole || "").toLowerCase();
    if (["national_manager", "regional_manager", "area_manager", "campaign_manager"].includes(teamRole)) return "manager";
    if (teamRole === "team_leader") return "team_leader";
    return "fundraiser";
  }
  return null;
}

function storageKey(user, tourKey) {
  const uid = user?.id || user?.email || "anon";
  return `taptrack_tour_${TOUR_VERSION}_${uid}_${tourKey}`;
}

// Public helper: launch from anywhere
export function openOnboardingTour() {
  window.dispatchEvent(new CustomEvent("taptrack:open-tour"));
}

export default function OnboardingTour({ forceShow = false } = {}) {
  const { user } = useAuth();
  const tourKey = useMemo(() => resolveTourKey(user), [user]);
  const tour = tourKey ? TOURS[tourKey] : null;
  const [open, setOpen] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    if (!user || !tour) return;
    if (forceShow) {
      setStepIdx(0);
      setOpen(true);
      return;
    }
    try {
      const k = storageKey(user, tourKey);
      const completed = localStorage.getItem(k);
      if (!completed) setOpen(true);
    } catch (_) { /* no-op */ }

    const handleOpen = () => {
      setStepIdx(0);
      setOpen(true);
    };
    window.addEventListener("taptrack:open-tour", handleOpen);
    return () => window.removeEventListener("taptrack:open-tour", handleOpen);
  }, [user, tour, tourKey, forceShow]);

  if (!user || !tour || !open) return null;

  const steps = tour.steps || [];
  const total = steps.length;
  const isLast = stepIdx >= total - 1;

  const markCompleted = () => {
    try { localStorage.setItem(storageKey(user, tourKey), new Date().toISOString()); } catch (_) { /* no-op */ }
  };
  const handleSkip = () => { markCompleted(); setOpen(false); };
  const handleComplete = () => { markCompleted(); setOpen(false); };
  const handleNext = () => { if (isLast) handleComplete(); else setStepIdx((i) => i + 1); };
  const handleBack = () => setStepIdx((i) => Math.max(0, i - 1));

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="onboarding-tour"
      aria-modal="true"
      role="dialog"
    >
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={handleSkip}
        data-testid="onboarding-tour-backdrop"
      />
      <div
        className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl border border-slate-200 shadow-2xl overflow-hidden"
        data-testid="onboarding-tour-card"
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#052E16] flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-[#86EFAC]" />
            </div>
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                Guided Walkthrough
              </div>
              <div className="text-[15px] font-semibold text-slate-900 tracking-tight" data-testid="onboarding-tour-title">
                {tour.title}
              </div>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="text-slate-400 hover:text-slate-700 transition-colors p-1 -mr-1"
            aria-label="Close walkthrough"
            data-testid="onboarding-tour-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 sm:py-6">
          <div className="text-[13px] text-slate-600 leading-[1.6] mb-4" data-testid="onboarding-tour-intro">
            {tour.intro}
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid={`onboarding-tour-step-${stepIdx}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono font-semibold text-slate-400 tracking-wider">
                {String(stepIdx + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
              </span>
              <span className="h-px flex-1 bg-slate-200" />
            </div>
            <h3 className="text-[15px] font-semibold text-slate-900 tracking-tight mb-1.5" data-testid="onboarding-tour-step-title">
              {steps[stepIdx]?.title}
            </h3>
            <p className="text-[13.5px] text-slate-600 leading-[1.6]" data-testid="onboarding-tour-step-body">
              {steps[stepIdx]?.body}
            </p>
          </div>
          <div className="flex items-center justify-center gap-1.5 mt-5" data-testid="onboarding-tour-progress">
            {steps.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === stepIdx ? "w-6 bg-[#15803D]" : "w-1.5 bg-slate-300"}`}
              />
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3 bg-slate-50/50">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSkip}
            className="text-slate-500 hover:text-slate-700 text-[13px]"
            data-testid="onboarding-tour-skip"
          >
            Skip
          </Button>
          <div className="flex items-center gap-2">
            {stepIdx > 0 && (
              <Button variant="outline" size="sm" onClick={handleBack} data-testid="onboarding-tour-back" className="text-[13px]">
                <ChevronLeft className="h-3.5 w-3.5 mr-1" />
                Back
              </Button>
            )}
            {!isLast ? (
              <Button size="sm" onClick={handleNext} className="bg-[#15803D] hover:bg-[#166534] text-white text-[13px]" data-testid="onboarding-tour-next">
                Next
                <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            ) : (
              <Button size="sm" onClick={handleComplete} className="bg-[#15803D] hover:bg-[#166534] text-white text-[13px]" data-testid="onboarding-tour-complete">
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                {tour.cta || "Got it"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Small standalone replay button — drop into headers
export function ReplayTourButton({ className = "" }) {
  return (
    <button
      onClick={openOnboardingTour}
      className={`inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-800 transition-colors ${className}`}
      data-testid="onboarding-tour-replay"
      aria-label="Replay walkthrough"
      title="Replay walkthrough"
    >
      <HelpCircle className="h-3.5 w-3.5" />
      <span className="hidden sm:inline">Tour</span>
    </button>
  );
}
